import { useState, useEffect, useRef } from 'react'
import { Alert, Button, IconButton, Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { LibraryBooks, TextSnippet } from '@mui/icons-material'
import { isMobile, useMobileOrientation } from 'react-device-detect'
import { checkFile, parseBotDate, trackEvent } from '../Utilities'
import { setFeatureFlag } from '../../api/dataSlice'
import { useKeycloak } from '../Keycloak'
import { Accordion, AccordionSummary, AccordionDetails } from '../controls/Accordion'
import { setDiaryActions } from '../chatbot/ChatManager'
import DatePicker from '../controls/DatePicker'
import CameraSelection from '../controls/CameraSelection'
import PdfDialog from '../controls/PdfDialog'

// gated by MYSITE route access (role check skipped on `protected: false`); a per-timestamp PDF HEAD-check drives the disabled state and the inline "no file" alert above the button
export default function DiaryAccordion({ expanded, onChangeExpanded, isPortrait, loading, timestamp, setTimestamp, shouldDisableDate, footerOpen, setFooterOpen, initialCameraId, onCameraIdChange }) {

    const { t } = useTranslation()
    const { CAMERAS, LAYOUT: { TITLE }, BASEURL, ROUTES: { MYSITE } } = window.conf
    const { isPortrait: mobilePortrait } = useMobileOrientation()
    const { hasRouteAccess, isLoggedIn } = useKeycloak()

    const dispatch = useDispatch()
    const hasMysite = hasRouteAccess(MYSITE)

    const [cameraId, setCameraId] = useState(initialCameraId ?? (CAMERAS?.length ? CAMERAS[0].id : 0)) // initialCameraId (ArchiveControls) first -> survives mobile-portrait footer unmount
    const [openDialog, setOpenDialog] = useState(false)
    const [fileExists, setFileExists] = useState(null) // null = checking, true/false = result of HEAD probe

    const hasDiary = hasMysite
    const moreThanOneCam = CAMERAS.length > 1
    const selectedCam = CAMERAS.find(camera => camera.id === cameraId)
    const selectedDate = timestamp.format("YY-MM-DD")
    const filePath = `${BASEURL}cam${selectedCam.id + 1}/reports/${selectedDate}.pdf`
    const title = TITLE ? `${TITLE}_` : ""
    const camera = selectedCam.name
        ? `${selectedCam.name}_`
        : moreThanOneCam
            ? `${t("events.camera")} ${selectedCam.id + 1}_`
            : ""
    const targetFilename = `${title}${camera}${t("events.construction_diary.construction_diary")}_${selectedDate}`
    const stateRef = useRef({})
    stateRef.current = { cameraId, timestamp, fileExists, allCameraIds: CAMERAS.map(c => c.id), setTimestamp }

    // chatbot registry — refs feed live state and the latest setTimestamp without stale closures
    useEffect(() => {
        setDiaryActions({
            getSelectedCameraId: () => stateRef.current.cameraId,
            getAvailableCameraIds: () => stateRef.current.allCameraIds || [],
            getDate: () => stateRef.current.timestamp?.isValid?.() ? stateRef.current.timestamp.format('YYYY-MM-DD') : null,
            getFileExists: () => !!stateRef.current.fileExists,
            setSelectedCameraId: (id) => setCameraId(id),
            setDate: (dateStr) => {
                const date = parseBotDate(dateStr)
                if (date && typeof stateRef.current.setTimestamp === 'function') {
                    stateRef.current.setTimestamp(date)
                }
            },
            openDialog: () => setOpenDialog(true),
        })
        return () => setDiaryActions(null)
    }, [])

    useEffect(() => {
        dispatch(setFeatureFlag({ name: 'hasDiary', value: !!hasDiary }))
    }, [dispatch, hasDiary])

    // push local selection up so it persists across footer close/reopen
    useEffect(() => {
        onCameraIdChange?.(cameraId)
    // onCameraIdChange is a stable setState from the parent — not in deps on purpose
    }, [cameraId]) // eslint-disable-line react-hooks/exhaustive-deps

    // per-timestamp PDF check: drives the disabled state of the "show" button (and the inline alert above it) when the user picks a different date
    useEffect(() => {
        if (!hasMysite) return
        let isMounted = true
        setFileExists(null) // back to "checking" while the new HEAD request is in flight — keeps the button disabled, hides the stale alert
        const checkPdf = async () => {
            const { exists, contentType } = await checkFile(filePath)
            if (!isMounted) return
            setFileExists(exists && contentType === "pdf")
        }
        checkPdf()
        return () => { isMounted = false }
    }, [filePath, hasMysite])

    const onOpenDialog = () => {
        setOpenDialog(true)
        trackEvent(t("events.construction_diary.construction_diary", { lng: "de" }), t("actions.create", { lng: "de" }))
    }

    const onOpenDrawer = () => {
        if (setFooterOpen && !footerOpen) {
            setFooterOpen()
        }
    }

    return (
        <>
            <Accordion
                expanded={expanded === 'diary'}
                onChange={onChangeExpanded('diary')}
                isPortrait={isPortrait}
                disabled={!hasDiary}
                disabledTooltip={!isLoggedIn() ? t('msg.sign_in_to_unlock') : undefined}
            >
                <AccordionSummary className="accordion-with-subtitle">
                    <Box>
                        <Typography>{t("events.construction_diary.construction_diary")}</Typography>
                        <Typography className="accordion-subtitle">{`${t("events.construction_diary.analysis")} & ${t("weather.weather")}`}</Typography>
                    </Box>
                </AccordionSummary>

                <AccordionDetails>
                    {isMobile && mobilePortrait && !footerOpen ? (
                        <IconButton
                            aria-label="construction diary"
                            color="primary"
                            onClick={onOpenDrawer}
                            sx={{ pointerEvents: "all" }}
                            disabled={loading || !hasDiary}
                        >
                            <TextSnippet sx={{ fontSize: "2rem" }} />
                        </IconButton>
                    ) : (
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
                            {moreThanOneCam && (
                                <CameraSelection
                                    value={cameraId}
                                    onChange={setCameraId}
                                />
                            )}

                            <DatePicker
                                timestamp={timestamp}
                                setTimestamp={setTimestamp}
                                loading={loading}
                                shouldDisableDate={shouldDisableDate}
                                pb={2}
                                withoutToday
                            />

                            {hasDiary && fileExists === false && (
                                <Alert
                                    variant="filled"
                                    severity="error"
                                    sx={{ mb: 2, alignItems: "center", userSelect: "none" }}
                                >
                                    {t("msg.no_file")}
                                </Alert>
                            )}

                            <Button
                                onClick={onOpenDialog}
                                startIcon={<LibraryBooks />}
                                disabled={loading || !hasDiary || !fileExists}
                                variant="contained"
                                size='large'
                                fullWidth
                            >
                                {t("actions.show")}
                            </Button>
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>

            <PdfDialog
                open={openDialog}
                setOpen={setOpenDialog}
                fileExists={fileExists}
                filePath={filePath}
                targetFilename={targetFilename}
            />
        </>
    )
}
